import { FCC, Section, SubmitButton } from "@dgs/core";
import { Form, Formik } from "formik";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DataFieldValues, RegistrationFormStaticStepResource, RegistrationGuestAttribute } from "../../shared/types";
import { BlockRenderer } from "../BlockRenderer";
import { useRegistrationForm } from "../provider/registrationFormProviderContext";
import {
	createInitialValues,
	getSectionsFromStaticStep,
	toGuestAttributeChildrenFromSection,
} from "../registrationFormUtils";
import { RegistrationDefaultSection } from "../section/RegistrationDefaultSection";

const Wrapper = styled(Section)`
	padding-top: 1.5rem;
	padding-bottom: 1.5rem;
`;

const ButtonWrapper = styled.div`
	display: flex;
	justify-content: end;
`;

interface Props {
	step: RegistrationFormStaticStepResource;
	handleSubmit: (dataFieldValues: DataFieldValues) => void;
	guestAttributeValues?: RegistrationGuestAttribute[];
	submitButtonTitle?: string;
}

export const StaticStepForm: FCC<Props> = ({
	children,
	step,
	handleSubmit,
	guestAttributeValues,
	submitButtonTitle,
}) => {
	const { t } = useTranslation();
	const { validateStaticStep } = useRegistrationForm();

	const initialValues = useMemo(() => {
		return createInitialValues(
			getSectionsFromStaticStep(step).flatMap(({ section }) => toGuestAttributeChildrenFromSection(section)),
			guestAttributeValues,
		);
	}, [step, guestAttributeValues]);

	return (
		<Formik
			initialValues={initialValues}
			validate={(values) => validateStaticStep(step, values)}
			onSubmit={handleSubmit}
		>
			<Form>
				{step.children.map((sectionOrBlock) =>
					sectionOrBlock.entityType === "block" ? (
						<BlockRenderer
							key={`${sectionOrBlock.entityType}_${sectionOrBlock.block.id}`}
							block={sectionOrBlock.block}
						/>
					) : (
						<Wrapper
							key={`${sectionOrBlock.entityType}_${sectionOrBlock.section.id}`}
							color={sectionOrBlock.section.backgroundColor}
						>
							<RegistrationDefaultSection
								stepId={step.id}
								section={sectionOrBlock.section}
								validationRules={sectionOrBlock.section.validationRules}
							/>
						</Wrapper>
					),
				)}
				{!!children && <Wrapper color="content">{children}</Wrapper>}
				<Wrapper color="content">
					<ButtonWrapper>
						<SubmitButton>{submitButtonTitle || t("registration.submit")}</SubmitButton>
					</ButtonWrapper>
				</Wrapper>
			</Form>
		</Formik>
	);
};
